// Imports => React
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import clsx from "clsx";
import classnames from "classnames";

// Imports => Constants
import { ICONS } from "@opp/ui-kit";

// Imports => Utilities
import { AcIsSet, AcIsEmptyString, AcCopyToClipboard } from "@opp/ui-kit";

// Imports => Atoms
import { AcIcon } from "@opp/ui-kit";

const _CLASSES = {
  MAIN: "ac-copy-text",
  FIXED: "ac-copy-text--fixed",
  ICON: {
    MAIN: "ac-copy-text__icon",
    VISIBLE: "ac-copy-text__icon--visible",
    SUCCESS: "ac-copy-text__icon--success",
  },
  MESSAGE: "ac-copy-text__message",
};

const AcCopyText = ({
  t,
  className: _className,
  title,
  value,
  fixed = false,
  children,
  ...rest
}) => {
  const copyDelay = useRef(null);
  const visibleDelay = useRef(null);
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopiedState = () => {
    if (visibleDelay.current) clearTimeout(visibleDelay.current);
    if (copyDelay.current) clearTimeout(copyDelay.current);

    setVisible(true);
    setCopied(true);

    visibleDelay.current = setTimeout(() => {
      setVisible(false);
    }, 3000);

    copyDelay.current = setTimeout(() => {
      setCopied(false);
    }, 3600);
  };

  const handleClick = useCallback(
    (event) => {
      if (!AcIsSet(value) || AcIsEmptyString(value)) return;

      if (event && event.persist) event.persist();
      if (event && event.preventDefault) event.preventDefault();
      if (event && event.stopPropagation) event.stopPropagation();

      AcCopyToClipboard(null, value).then(handleCopiedState);
    },
    [value]
  );

  const getMessageClassNames = useMemo(() => {
    return clsx(_CLASSES.MESSAGE);
  }, []);

  const getIconClassNames = useMemo(() => {
    return clsx(
      _CLASSES.ICON.MAIN,
      visible && _CLASSES.ICON.VISIBLE,
      copied && _CLASSES.ICON.SUCCESS
    );
  }, [visible, copied]);

  const getMainClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN, fixed && _CLASSES.FIXED);
  }, [fixed]);

  const getTitle = useMemo(() => {
    if (!AcIsSet(title) || AcIsEmptyString(title))
      return t("copy-to-clipboard", "Copy to clipboard", { ns: "common" });

    return title;
  }, [title]);

  const renderIcon = useMemo(() => {
    let icon = ICONS.CONTENT_COPY;

    if (copied) icon = ICONS.CHECKBOX_MARKED_CIRCLE_OUTLINE;

    return (
      <AcIcon icon={icon} title={getTitle} className={getIconClassNames} />
    );
  }, [copied, visible, getTitle]);

  return (
    <i
      className={classnames(getMainClassNames, _className)}
      title={getTitle}
      onClick={handleClick}
      data-value={value}
      {...rest}
    >
      {children}
      {renderIcon}
      <em className={getMessageClassNames}>
        {t("copied", "Copied!", { ns: "common" })}
      </em>
    </i>
  );
};

export default AcCopyText;
